import { serviceUmbrellas } from "@/pages/servicesData";
import { blogPosts } from "@/pages/blogData";
import {
  cookiePolicy,
  privacyPolicy,
  termsOfService,
} from "@/pages/legalData";

const t = (text) => ({ text, size: null });

// One-sentence summaries for the homepage What We Do cards. Any umbrella
// created later in the admin panel falls back to its tagline.
const umbrellaCardSummaries = {
  "managed-services":
    "Ongoing device, patch and provisioning management that keeps every endpoint secure, current and compliant without burdening your team.",
  "project-services":
    "Focused projects that onboard, migrate, provision, refresh and retire devices smoothly at every stage of the endpoint lifecycle.",
  "technology-consulting":
    "Practical assessments, endpoint strategy, roadmaps and procurement guidance that align every technology decision with your business goals.",
};

const serviceContent = (service) => ({
  slug: service.slug,
  icon: service.icon,
  title: t(service.title),
  hook: t(service.hook),
  teaser: t(service.teaser),
  paragraphs: [...service.paragraphs],
  howWeHelp: [...service.howWeHelp],
  idealFor: [...service.idealFor],
  related: [...service.related],
});

const umbrellaContent = (umbrella) => ({
  slug: umbrella.slug,
  icon: umbrella.icon,
  badge: umbrella.badge,
  title: t(umbrella.title),
  hook: t(umbrella.hook),
  tagline: t(umbrella.tagline),
  paragraphs: [...umbrella.paragraphs],
  services: umbrella.services.map(serviceContent),
});

const legalPageContent = (page) => ({
  titleStart: t(page.titleStart),
  titleAccent: t(page.titleAccent),
  updated: t(page.updated),
  intro: t(page.intro),
  sections: page.sections.map((section) => ({
    heading: t(section.heading),
    paragraphs: [...section.paragraphs],
    bullets: section.bullets ? [...section.bullets] : [],
  })),
});

export const buildDefaultContent = () => ({
  // Admin-editable FAQ overrides, keyed by page. Empty by default so every
  // page falls back to its auto-generated FAQ set (see resolveFaqs). When the
  // admin saves a list here it replaces the auto set for that page.
  faqs: {},
  global: {
    brandSub: t("DEVICE SERVICES"),
    logoImage: "",
    nav: {
      home: t("HOME"),
      services: t("SERVICES"),
      blog: t("BLOG"),
      about: t("ABOUT US"),
      contact: t("CONTACT"),
    },
    ctaLabel: t("Get in Touch"),
    altContactLabel: t("Prefer email? Send us a message"),
    calendlyUrl: "https://calendly.com/consultations-itdeviceservices",
    contactBar: {
      title: t("READY TO POWER\nYOUR BUSINESS?"),
      text: t("Let's build a smarter,\nstronger and more secure\nIT environment."),
      ctaLabel: t("GET IN TOUCH"),
      email: "info@itdeviceservices.com",
      phone: "+1 (682) 231-1534",
    },
    socials: {
      linkedin: "https://www.linkedin.com/company/itdeviceservices",
      youtube: "",
    },
    footer: {
      copyright: t("\u00a9 IT Device Services. All rights reserved."),
      privacyLabel: t("Privacy Policy"),
      termsLabel: t("Terms of Service"),
      cookiesLabel: t("Cookie Policy"),
    },
    quickInquiry: {
      title: t("Quick Inquiry"),
      text: t("Have a question? Send us a quick message and our team will get back to you shortly."),
      namePlaceholder: t("Your name"),
      emailPlaceholder: t("Your email"),
      messagePlaceholder: t("How can we help?"),
      submitLabel: t("Send"),
    },
    cookieBanner: {
      text: t("We use a small number of cookies to keep this site running smoothly. See our"),
      acceptLabel: t("Accept"),
      declineLabel: t("Decline"),
    },
    // Full contact form shown at the bottom of Services, About, Blog, Blog
    // Post, Service Detail and Contact pages (not the homepage).
    contactForm: {
      title1: t("Prefer email?"),
      title2: t("Send us a"),
      accent: t("message"),
      paragraph: t("Tell us a little about your environment and\nwe will get back to you within one business day."),
      points: [
        t("No sales pressure, just straight answers"),
        t("Replies within one business day"),
        t("Your details stay private"),
      ],
      submitLabel: t("Send Message"),
    },
  },
  home: {
    sectionOrder: ["hero", "stats", "whatwedo", "whychoose"],
    hero: {
      image: "",
      title1: t("SIMPLIFY YOUR IT."),
      title2: t("POWER YOUR"),
      titleAccent: t("DEVICES."),
      paragraph: t("Modern IT management and powerful solutions\nto keep your business secure, productive\nand always ahead."),
      cards: [
        { slug: "technology-refresh", title: t("TECHNOLOGY REFRESH"), sub: t("Modern, secure hardware") },
        { slug: "device-provisioning", title: t("DEVICE PROVISIONING"), sub: t("Ready and protected day one") },
        { slug: "patch-management", title: t("PATCH MANAGEMENT"), sub: t("Always current, always secure") },
        { slug: "device-migration", title: t("DEVICE MIGRATION"), sub: t("Safe, seamless transitions") },
      ],
      screen: {
        statusSmall: t("All Systems"),
        statusStrong: t("Operational"),
        rows: [t("Devices"), t("Security"), t("Updates")],
      },
    },
    stats: {
      items: [
        { value: t("3"), label: t("SERVICE AREAS"), sub: t("Managed, Project, Consulting") },
        { value: t("12+"), label: t("SERVICES"), sub: t("Across the device lifecycle") },
        { value: t("24/7"), label: t("DEVICE MANAGEMENT"), sub: t("Monitored and maintained") },
        { value: t("100%"), label: t("LIFECYCLE COVERAGE"), sub: t("Onboarding to disposal") },
        { value: t("15+"), label: t("YEARS"), sub: t("Of IT excellence") },
      ],
    },
    whatwedo: {
      title1: t("Complete endpoint\nlifecycle"),
      accent: t("management."),
      paragraph: t("From onboarding new devices to ongoing management, technology refreshes and planning, we help businesses manage every stage of the endpoint lifecycle."),
      learnMoreLabel: t("LEARN MORE"),
      cards: serviceUmbrellas.map((umbrella) => ({
        slug: umbrella.slug,
        icon: umbrella.icon,
        title: t(umbrella.title.toUpperCase()),
        body: t(umbrellaCardSummaries[umbrella.slug] || umbrella.tagline),
      })),
    },
    whychoose: {
      image: "",
      title1: t("Technology is complex."),
      title2: t("We make it"),
      accent: t("simple"),
      paragraph: t("We combine cutting-edge technology with expert\nprovisioning and deployment to give you peace of mind\nand room to focus on what matters most, your business."),
      features: [
        { title: t("Provisioning & Deployment"), body: t("Devices arrive configured,\nsecured and ready\nto work from day one.") },
        { title: t("Cost Efficient"), body: t("Optimize performance\nand reduce downtime\nwith smart solutions.") },
        { title: t("Scalable Solutions"), body: t("From startups to\nenterprises, we grow\nwith your needs.") },
      ],
    },
  },
  services: {
    overview: {
      titleStart: t("Complete Endpoint"),
      titleAccent: t("Lifecycle Management."),
      intro: t("From onboarding new devices to ongoing management, technology refreshes, and planning, we help businesses manage every stage of the endpoint lifecycle through expert project services, managed services, and technology consulting."),
      learnMoreLabel: t("LEARN MORE"),
      howWeHelpLabel: t("How We Help"),
      idealForLabel: t("Ideal For"),
      relatedLabel: t("Related Services"),
      banner: {
        heading: t("Not sure where to start?"),
        text: t("Tell us about your environment and we will map the right services to your business."),
      },
    },
    umbrellas: serviceUmbrellas.map(umbrellaContent),
  },
  about: {
    titleStart: t("About"),
    titleAccent: t("Us."),
    intro: t("We specialize in endpoint lifecycle management, helping organizations plan, deploy, manage, refresh, and retire technology throughout its entire lifecycle."),
    image: "",
    lead: {
      title1: t("Endpoint lifecycle"),
      title2: t("management, done"),
      accent: t("properly."),
      paragraphs: [
        "We specialize in endpoint lifecycle management, helping organizations plan, deploy, manage, refresh, and retire technology throughout its entire lifecycle. By combining modern device management, project services, and strategic consulting, we deliver technology solutions that improve security, productivity, and operational efficiency.",
        "Most businesses do not need more technology. They need the technology they already own to work reliably, stay secure, and get out of the way. That is the problem we solve, from the first device a new hire signs into through to the day that device is retired responsibly.",
        "We work as an extension of your team. Whether you have no internal IT function or an experienced team that needs specialist support for a specific project, we scale to fit and hand back a clean, documented environment.",
      ],
    },
    approach: {
      title: t("How We Work"),
      intro: t("A simple, repeatable way of working that keeps projects predictable and day-to-day operations quiet."),
      items: [
        {
          title: t("Understand first"),
          body: t("Before we recommend anything we look at how your environment runs today, where the friction is, and what your business actually needs from its devices."),
        },
        {
          title: t("Standardize everything"),
          body: t("Consistency is what makes an endpoint estate manageable. We build repeatable standards for provisioning, security, patching and support so every device behaves the same way."),
        },
        {
          title: t("Stay out of the way"),
          body: t("Good endpoint management is invisible. We do the operational work in the background so your people can get on with their jobs."),
        },
      ],
    },
    values: {
      title: t("What Guides Us"),
      items: [
        {
          title: t("Practical over theoretical"),
          body: t("We recommend what will work in your environment, not what looks impressive on a slide."),
        },
        {
          title: t("Vendor independent"),
          body: t("We are not tied to a single manufacturer. Our guidance follows your requirements and your budget."),
        },
        {
          title: t("Security by default"),
          body: t("Every device we touch leaves with security settings and organizational policies already applied."),
        },
        {
          title: t("Accountable end to end"),
          body: t("We own the full lifecycle, including the unglamorous parts like data handling and responsible disposal."),
        },
      ],
    },
    banner: {
      heading: t("Want to talk it through?"),
      text: t("Tell us about your environment and we will tell you honestly where we can help."),
    },
  },
  blog: {
    titleStart: t("The"),
    titleAccent: t("Blog."),
    intro: t("Practical thinking on device management, hardware lifecycles and security, written for people who run businesses, not data centers."),
    readMoreLabel: t("READ MORE"),
    banner: {
      heading: t("Want this handled for you?"),
      text: t("Everything we write about is something we do. Let's talk about your environment."),
    },
    posts: blogPosts.map((post) => ({
      slug: post.slug,
      category: t(post.category),
      date: t(post.date),
      title: t(post.title),
      excerpt: t(post.excerpt),
      paragraphs: [...post.paragraphs],
    })),
  },
  legal: {
    "privacy-policy": legalPageContent(privacyPolicy),
    "terms-of-service": legalPageContent(termsOfService),
    "cookie-policy": legalPageContent(cookiePolicy),
  },
  contact: {
    titleStart: t("Get in"),
    titleAccent: t("Touch."),
    intro: t("Have a question about our services or need a quote? Reach out and our team will get back to you within one business day."),
    hours: t("Monday - Friday, 9am - 6pm CST"),
    addressLabel: t("Based in"),
    address: t("Dallas-Fort Worth Metroplex, TX"),
  },
});
